import React from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field as FormikField } from 'formik';
import * as Yup from 'yup';

import { AuthShell } from '../../design-system/components/shells';
import {
  AuthBanner,
  AuthField,
  AuthFields,
  AuthFootnote,
  AuthGhostLink,
  AuthInput,
  AuthPasswordInput,
  AuthSubmit,
} from '../../design-system/patterns/auth';
import { routes } from '../../routes/routes';

/**
 * Login, the view. Pure: every input is a prop, every output is a callback.
 *
 * The container (`login.jsx`) owns `useAuth`, the redirect, analytics and the
 * attempt counter. This file owns what the user sees, which is why the state
 * explorer can mount it with fixture props and get the real screen back rather
 * than a mock of it.
 *
 * TWO DELIBERATE DIFFERENCES FROM THE UPSTREAM TEMPLATE
 * ----------------------------------------------------
 * 1. NO "Continue with Google". The template leads with it, and the reasoning
 *    there is sound: a user who signed up with Google has no password, and
 *    putting the email form first walks them into failing a password they never
 *    set. But this app has no Google identity provider. `grep -ri google src`
 *    finds one Places autocomplete component and nothing else. Rendering the
 *    button would be shipping a control that cannot work. When Google sign-in
 *    is added it goes ABOVE the fields, with the "or" divider, for that reason.
 *
 * 2. The password error carries "Forgot password?" beside it rather than above
 *    the submit. Login is the case the `foot` slot in `AuthField` exists for:
 *    the moment the password is wrong is the moment the reset link stops being
 *    chrome and becomes the useful thing on the page.
 *
 * The admin entrance is not on this screen. `/admin-login` is its own route and
 * the app never linked to it from here.
 */

export const loginValidationSchema = Yup.object({
  email: Yup.string().email('Enter a valid email address').required('Email address is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

export function LoginView({
  onSubmit,
  loading = false,
  error,
  message,
  initialValues = { email: '', password: '' },
  initialErrors,
  initialTouched,
  /* The path `ProtectedRoute` bounced them off, from `location.state.from`.
     Undefined for someone who came to sign in of their own accord. */
  returningTo,
  /* Why they were sent here, when the path alone does not say. `/cart` is
     public, so a bounce FROM it is not self-explanatory the way `/checkout`
     is — see `CartPage`'s guard for why it sends them back there rather than
     to a checkout that has no handoff to read yet. */
  returnReason,
}) {
  /* A buyer sent here mid-purchase is not "picking up their watchlist", and
     the link out should not be to the marketplace — that is away from the cart
     they were paying for. `/cart` is public, so it is somewhere they can
     actually go while still signed out. Every other protected page keeps the
     ordinary greeting: "sign in to see your orders" is not worth a variant. */
  const fromCheckout = returnReason === 'checkout' || returningTo === routes.CHECKOUT;

  return (
    <AuthShell
      title={fromCheckout ? 'Sign in to check out' : 'Welcome back'}
      subtitle={
        fromCheckout
          ? 'Your cart is saved. Sign in and we will take you straight back to it.'
          : 'Sign in to pick up your watchlist and open orders.'
      }
      back={
        fromCheckout
          ? { to: routes.CART, label: 'Back to your cart' }
          : { to: routes.MARKETPLACE, label: 'Back to marketplace' }
      }
      footer={
        <AuthFootnote>
          New to Sleevr? <Link to={routes.SIGNUP}>Create an account</Link>
        </AuthFootnote>
      }
    >
      <AuthBanner tone="success">{message}</AuthBanner>
      <AuthBanner tone="error">{error}</AuthBanner>

      <Formik
        initialValues={initialValues}
        initialErrors={initialErrors}
        initialTouched={initialTouched}
        validationSchema={loginValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched }) => {
          const busy = isSubmitting || loading;
          const emailError = touched.email && errors.email ? errors.email : undefined;
          const passwordError = touched.password && errors.password ? errors.password : undefined;

          return (
            <Form noValidate>
              <AuthFields>
                <AuthField label="Email address" error={emailError}>
                  <FormikField
                    as={AuthInput}
                    name="email"
                    type="email"
                    placeholder="you@example.co.za"
                    autoComplete="username"
                    disabled={busy}
                  />
                </AuthField>

                <AuthField
                  label="Password"
                  error={passwordError}
                  foot={
                    <AuthGhostLink to={routes.FORGET}>Forgot password?</AuthGhostLink>
                  }
                >
                  <FormikField
                    as={AuthPasswordInput}
                    name="password"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={busy}
                  />
                </AuthField>
              </AuthFields>

              <AuthSubmit loading={busy} loadingLabel="Signing in">
                Sign in
              </AuthSubmit>
            </Form>
          );
        }}
      </Formik>
    </AuthShell>
  );
}

export default LoginView;
